import { CommonModule, NgIf } from "@angular/common";
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatPaginator } from "@angular/material/paginator";
import { ComentariosService } from "@src/app/servicios/comentarios/comentarios.service";
import { CookieService } from "ngx-cookie-service";
import { first, firstValueFrom } from "rxjs";
import { ConfigService } from "src/app/servicios/config/config.service";
import { loggedInValidator } from "src/app/utils/user.validator";
import { environment } from "src/environments/environment";

@Component({
  selector: "app-send-comment-textarea",
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatButtonModule, NgIf],
  providers: [ComentariosService],
  templateUrl: "./send-comment-textarea.component.html",
  styleUrl: "./send-comment-textarea.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SendCommentTextareaComponent implements OnInit {
  @Input() idVideo;
  @Input() idParent;
  @Input() compact: boolean = false;
  @Output() onSendComment = new EventEmitter();
  comentarioForm: FormGroup;
  @ViewChild(MatPaginator) paginator?: MatPaginator;
  @ViewChild("textarea") textarea: ElementRef;

  expanded: boolean = false;
  numeroComentariosPagina = 10;
  showFirstLastButtons = false;
  pageSizeOptions: any;
  shake: boolean = false;
  login: boolean = false;
  showBtnEnvio: boolean = true;
  showTextarea: boolean = true;
  comentariosAnonimos: boolean = true;
  anonId: number;
  textoBtnEnvio: string;
  notaInformativa: string;
  placeholder: string;

  constructor(
    private comentariosService: ComentariosService,
    private cookieService: CookieService,
    private formBuilder: FormBuilder,
    public dialog: MatDialog,
    private configService: ConfigService,
    private elementRef: ElementRef,
  ) {
    this.comentarioForm = this.formBuilder.group({
      comentario: ["", { validators: [Validators.required, Validators.minLength(3), Validators.pattern(/^(?!\s)[\s\S]*$/)] }],
    });
  }

  ngOnInit(): void {
    this.loadConfig();
  }

  loadConfig() {
    if (this.cookieService.get("userLogin" + environment.name)) {
      this.login = true;
    }
    const selectorName = "app-comentarios";
    const configPrensa = this.configService.getConfigPrensa();
    if (configPrensa) {
      configPrensa.pipe(first()).subscribe((resultConfig) => {
        if (resultConfig) {
          const configBloque = resultConfig.noticia[selectorName];

          this.showBtnEnvio = configBloque.mostrar_btn_enviar === "true";
          this.comentariosAnonimos = configBloque.comentarios_anonimos === "true";
          if (configBloque?.id_usuario_anonimo) this.anonId = configBloque?.id_usuario_anonimo;
          if (!this.comentariosAnonimos) {
            this.comentarioForm.get("comentario").setValidators(loggedInValidator(this.login));
          }

          const fondoComentario = configBloque.mostrar_textarea === "true";
          this.textoBtnEnvio = configBloque.texto_btn_envio;
          this.notaInformativa = configBloque.nota_informativa;
          this.placeholder = configBloque.placeholder_textarea;

          const colorTituloComentarios = configBloque.color_titulo;
          const colorBtnEnvio = configBloque.color_btn_envio;
          const colorTextoBtnEnvio = configBloque.color_texto_btn_envio;
          this.elementRef.nativeElement.style.setProperty("--color-titulo-comentarios", colorTituloComentarios);
          this.elementRef.nativeElement.style.setProperty("--color-btn-envio", colorBtnEnvio);
          this.elementRef.nativeElement.style.setProperty("--color-texto-btn-envio", colorTextoBtnEnvio);
          if (fondoComentario) {
            const colorFondoComentarios = configBloque.color_fondo_comentarios;
            this.elementRef.nativeElement.style.setProperty("--color-background-comentarios", colorFondoComentarios);
          }
        }
      });
    }
  }

  enviarComentario(): void {
    if (!this.comentarioForm.valid) {
      this.shakeTextarea();
      return;
    }
    const trimmedComment = this.comentarioForm.value.comentario.trim();

    if (this.comentarioForm.value.comentario && trimmedComment != "") {
      let userId = this.anonId ?? 1000318;
      let activo = 0;
      if (this.login) {
        userId = JSON.parse(this.cookieService.get("userLogin" + environment.name)).id;
        activo = 1;
      }
      const sendComment = this.comentariosService.sendComentario(userId, this.idVideo, trimmedComment, this.idParent ? this.idParent : null);
      if (sendComment) {
        firstValueFrom(sendComment).then((response) => {
          if (!response) return;
          if (Number(response?.status) === 202) {
            this.comentarioForm.get("comentario").setErrors({ serverError: response });
          } else if (Number(response?.status) === 201) {
            if (response?.body.moderation) {
              this.resetArea();
              this.comentarioForm.get("comentario").setErrors({ serverError: response.body.message });
              return;
            }
            this.resetArea();
            const comment = {
              id: response.body.id,
              userId: userId,
              comment: trimmedComment,
              active: activo,
              profundidad: 1,
              fecha_hora: new Date(),
              mensaje: trimmedComment,
              nombre: this.cookieService.get("userLogin" + environment.name) ? JSON.parse(this.cookieService.get("userLogin" + environment.name)).nombre : "Anónimo",
              score: 0,
            };
            this.onSendComment.emit(comment);
          }
        });
      }
    }
  }

  resetArea() {
    this.comentarioForm.value.comentario = "";
    this.textarea.nativeElement.value = "";
    this.comentarioForm.get("comentario").markAsUntouched();
    this.textarea.nativeElement.blur();
  }

  /**
   * Establece la variable `shake` en true, luego, después de 200 milisegundos, la vuelve a establecer en falso.
   * Se utiliza para animar el textarea al enviar un comentario inválido haciendo que tiemble.
   */
  shakeTextarea() {
    this.shake = true;
    setTimeout(() => {
      this.shake = false;
    }, 200);
  }

  /**
   * Cambia la propiedad `expanded` a `true` para expandir la caja de comentarios
   */
  expand() {
    if (!this.expanded) {
      this.expanded = true;
    }
  }
}
